import React, { useCallback, useEffect, useState } from 'react';
import { Link2, Link2Off, RefreshCw, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../../ui/button';

// Website Builder → Link Check: scans LIVE builder pages for broken internal links and reports the exact page + element.
export const LinkCheckPanel = ({ api, onOpenPage }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = useCallback(() => { setBusy(true); api.linkCheck().then(setData).catch(() => setData({ error: true })).finally(() => setBusy(false)); }, [api]);
  useEffect(() => { run(); }, [run]);

  return (
    <div data-testid="link-check-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="!text-xl font-semibold text-neutral-900 flex items-center gap-2"><Link2 className="w-5 h-5" />Broken Link Check</h2>
          <p className="text-sm text-neutral-500">Checks every button, CTA, card link and rich-text link on live builder pages against published pages, redirects and coded routes. External, mailto and tel links are skipped.</p>
        </div>
        <Button variant="outline" onClick={run} disabled={busy} data-testid="link-check-run"><RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />{busy ? 'Scanning…' : 'Re-scan'}</Button>
      </div>
      {!data && <p className="text-sm text-neutral-500">Scanning…</p>}
      {data?.error && <p className="text-sm text-red-600">Could not run the link check.</p>}
      {data && !data.error && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl" data-testid="link-check-summary">
            {[['Pages scanned', data.summary.pages_scanned], ['Pages with issues', data.summary.pages_with_issues], ['Broken links', data.summary.broken_links]].map(([l, v]) => (
              <div key={l} className="border border-neutral-200 p-3"><p className="text-[11px] uppercase tracking-wide text-neutral-500">{l}</p><p className={`text-2xl font-semibold ${l === 'Broken links' && v > 0 ? 'text-red-600' : 'text-neutral-900'}`}>{v}</p></div>
            ))}
          </div>
          {data.pages.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="link-check-ok"><CheckCircle2 className="w-4 h-4" />No broken internal links found on live pages.</div>
          ) : data.pages.map((pg) => (
            <div key={pg.slug} className="border border-neutral-200 mb-4" data-testid={`link-check-page-${pg.slug}`}>
              <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50 border-b border-neutral-200">
                <p className="text-sm font-medium text-neutral-900">{pg.name} <span className="text-neutral-400 font-normal font-mono text-xs">{pg.path}</span></p>
                <span className="text-xs text-red-700 font-medium">{pg.broken.length} broken</span>
              </div>
              <ul className="divide-y divide-neutral-100">
                {pg.broken.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 px-4 py-3 text-sm" data-testid={`link-check-item-${pg.slug}-${i}`}>
                    <Link2Off className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-neutral-900">Section {b.section_index} · <span className="font-medium">{b.section_title}</span> <span className="text-neutral-400">({b.section_type}/{b.section_layout})</span></p>
                      <p className="text-neutral-600">Element “{b.element}” → <code className="text-xs bg-neutral-100 px-1">{b.href}</code></p>
                      <p className="text-xs text-red-600 mt-0.5">{b.reason}</p>
                    </div>
                    {onOpenPage && <Button size="sm" variant="outline" onClick={() => onOpenPage(pg.slug, { sectionId: b.section_id })} data-testid={`link-check-fix-${pg.slug}-${i}`}>Open section <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </div>
  );
};
